import { activePage } from "../native/pages";
import type { EngineDocument } from "../scene/model";
import type { ImageStore } from "./image-store";
import { NativeCanvasRenderer } from "./native-canvas";
import { effectBounds } from "./native-effects-raster";

/** Bake the current appearance, including blend backdrops, into a transparent layer. */
export function rasterizeMerge(
  document: EngineDocument,
  ids: ReadonlySet<string>,
  images: ImageStore,
) {
  const page = activePage(document),
    nodes = page.nodes.filter((n) => ids.has(n.id));
  const boxes = nodes.map(effectBounds),
    x = Math.floor(Math.min(...boxes.map((b) => b.x))),
    y = Math.floor(Math.min(...boxes.map((b) => b.y))),
    width = Math.ceil(Math.max(...boxes.map((b) => b.x + b.width)) - x),
    height = Math.ceil(Math.max(...boxes.map((b) => b.y + b.height)) - y),
    scale = Math.min(
      4,
      8192 / width,
      8192 / height,
      Math.sqrt(16000000 / (width * height)),
    );
  if (scale < 1)
    throw new Error(
      "Объединение превышает допустимый размер без потери качества. Уменьшите область или объедините меньше слоёв.",
    );
  const top = Math.max(...page.nodes.map((n, i) => (ids.has(n.id) ? i : -1))),
    prefix = page.nodes.slice(0, top + 1),
    frame = {
      camera: { x: -x * scale, y: -y * scale, zoom: scale },
      width: Math.ceil(width * scale),
      height: Math.ceil(height * scale),
      dpr: 1,
    };
  const surface = globalThis.document.createElement("canvas"),
    renderer = new NativeCanvasRenderer(surface, images);
  const draw = (
    items: typeof nodes,
    selected?: ReadonlySet<string>,
    backdrop = false,
  ) => {
    renderer.draw(
      {
        ...frame,
        document: { ...document, pages: [{ ...page, nodes: items }] },
      },
      true,
      selected,
      false,
      backdrop,
    );
    return surface
      .getContext("2d")!
      .getImageData(0, 0, surface.width, surface.height);
  };
  try {
    const output = draw(page.nodes, ids),
      data = output.data;
    // Preserve the ordinary isolated raster when no selected layer needs its backdrop.
    // A non-contiguous selection also needs compensation for intervening layers.
    const first = page.nodes.findIndex((n) => ids.has(n.id));
    if (
      nodes.some(
        (n) =>
          n.appearance.blendMode && n.appearance.blendMode !== "source-over",
      ) ||
      prefix.slice(first).some((n) => !ids.has(n.id))
    ) {
      const target = draw(prefix, undefined, true).data,
        below = draw(
          prefix.filter((n) => !ids.has(n.id)),
          undefined,
          true,
        ).data;
      for (let i = 0; i < data.length; i += 4) {
        let a = data[i + 3]! / 255;
        if (!a) continue;
        const ta = target[i + 3]! / 255,
          ba = below[i + 3]! / 255;
        if (ba < 1) a = (ta - ba) / (1 - ba);
        let minimum = 0;
        for (let c = 0; c < 3; c++) {
          const t = (target[i + c]! / 255) * ta,
            b = (below[i + c]! / 255) * ba;
          if (b > 0) minimum = Math.max(minimum, 1 - t / b);
          if (b < 1) minimum = Math.max(minimum, (t - b) / (1 - b));
        }
        if (ba < 1 && (a < -0.01 || minimum > a + 0.025))
          throw new Error(
            "Эти наложения нельзя объединить без изменения прозрачности. Выберите также нижние слои.",
          );
        a = Math.min(1, Math.max(a, minimum, 0));
        data[i + 3] = Math.round(a * 255);
        a = data[i + 3]! / 255;
        for (let c = 0; c < 3; c++)
          data[i + c] = a
            ? (target[i + c]! * ta - below[i + c]! * ba * (1 - a)) / a
            : 0;
      }
    }
    // Trim transparent margins without resampling the 4× pixel grid.
    let left = surface.width,
      right = -1,
      upper = surface.height,
      bottom = -1;
    for (let yy = 0; yy < surface.height; yy++)
      for (let xx = 0; xx < surface.width; xx++)
        if (data[(yy * surface.width + xx) * 4 + 3]) {
          left = Math.min(left, xx);
          right = Math.max(right, xx);
          upper = Math.min(upper, yy);
          bottom = Math.max(bottom, yy);
        }
    if (right < 0)
      throw new Error(
        "В выбранных слоях нет видимых пикселей для объединения.",
      );
    const cropped = globalThis.document.createElement("canvas");
    cropped.width = right - left + 1;
    cropped.height = bottom - upper + 1;
    cropped.getContext("2d")!.putImageData(output, -left, -upper);
    return {
      source: cropped.toDataURL("image/png"),
      x: x + left / scale,
      y: y + upper / scale,
      width: cropped.width / scale,
      height: cropped.height / scale,
    };
  } finally {
    renderer.dispose();
  }
}
